import { useTranslation } from "react-i18next";
import PageHead from "@/components/PageHead";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { trackCTAClick } from "@/lib/analytics";
import JsonLd from "@/components/JsonLd";
import { contactPageSchema, breadcrumbSchema } from "@/lib/seo-schemas";
import FaqGroupsSection, { toFaqGroup } from "@/components/FaqGroupsSection";

const AnimatedSection = ({ children, className = "", style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) => {
  const [ref, isVisible] = useScrollAnimation<HTMLElement>();
  return (
    <section
      ref={ref}
      style={style}
      className={`${className} transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
    >
      {children}
    </section>
  );
};

const Collaborate = () => {
  const { t } = useTranslation();
  const collabGroups = (["collab"] as const)
    .map((k) => toFaqGroup(k, t(`faq.groups.${k}`, { returnObjects: true })))
    .filter(Boolean) as NonNullable<ReturnType<typeof toFaqGroup>>[];

  const advantages = [
    {
      title: t("collaborate.adv1Title"),
      desc: t("collaborate.adv1Desc"),
      items: ["Picture books & story series", "Product collaborations", "Preschool learning tools", "Apps & digital content"],
    },
    {
      title: t("collaborate.adv2Title"),
      desc: t("collaborate.adv2Desc"),
      items: ["Limited-edition collaborations", "Licensed product lines", "Brand-exclusive storytelling", "Educational programs"],
    },
    {
      title: t("collaborate.adv3Title"),
      desc: t("collaborate.adv3Desc"),
      items: ["Comfort items", "Routines (sleep, separation, transitions)", "Skill-building tools", "Social-emotional learning (SEL)"],
    },
    {
      title: t("collaborate.adv4Title"),
      desc: t("collaborate.adv4Desc"),
      items: ["Character guides", "Brand usage guidelines", "Clear educational philosophy", "Growing Instagram community"],
    },
  ];

  const howModes: string[] = t("collaborate.howModes", { returnObjects: true }) as string[];

  return (
    <>
      <PageHead
        title={t("collaborate.seoTitle")}
        description={t("collaborate.seoDesc")}
        canonical="https://queebi.com/collaborate"
        image="/images/og/collaborate-1200x630.jpg"
        imageAlt="Partner with Queebi Land — IP licensing and brand collaborations"
        keywords="Queebi collaborate, character IP licensing, children brand collaboration, educational content partnership, IP licensing, partnership, emotional learning"
      />
      <JsonLd
        data={[
          contactPageSchema({
            name: "Collaborate with Queebi Land",
            description: "Partner with Queebi Land for character IP licensing, co-creation, brand integration, or educational collaboration.",
            url: "https://queebi.com/collaborate",
            email: "hello@queebi.com.tw",
          }),
          breadcrumbSchema([
            { name: "Home", url: "https://queebi.com/" },
            { name: "Collaborate", url: "https://queebi.com/collaborate" },
          ]),
        ]}
      />

      <section className="bg-queebi-sky py-20 md:py-32">
        <div className="container max-w-3xl text-center">
          <h1 className="text-4xl md:text-5xl font-bold font-heading mb-8">{t("collaborate.heroTitle")}</h1>
          <p className="text-muted-foreground text-lg mb-4">{t("collaborate.heroP1")}</p>
          <p className="text-muted-foreground text-lg mb-4">{t("collaborate.heroP2")}</p>
          <p className="text-muted-foreground text-lg">{t("collaborate.heroP3")}</p>
        </div>
        <div className="container mt-10">
          <img src="/images/pages/collab-animals.png" alt="Queebi characters resting" className="mx-auto max-w-xl w-full" width={600} height={360} loading="lazy" />
        </div>
      </section>

      <AnimatedSection className="bg-cover bg-center py-20 md:py-32" style={{ backgroundImage: `url(/images/brand/bg-pattern.png)` }}>
        <div className="container max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-center mb-14">{t("collaborate.advantagesTitle")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {advantages.map((a, i) => (
              <div key={i} className="bg-card rounded-xl p-8 shadow-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <h3 className="font-bold text-lg mb-3">{a.title}</h3>
                <p className="text-base text-muted-foreground mb-4">{a.desc}</p>
                <ul className="space-y-2">
                  {a.items.map((item, j) => (
                    <li key={j} className="text-base flex items-start gap-2">
                      <span className="text-accent">✦</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection className="bg-card py-20 md:py-32">
        <div className="container max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-center mb-10">{t("collaborate.howTitle")}</h2>
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {howModes.map((m: string) => (
              <span key={m} className="bg-background rounded-full px-6 py-3 text-base font-bold">✔ {m}</span>
            ))}
          </div>
          <p className="text-center text-muted-foreground text-lg mb-10">{t("collaborate.howDesc")}</p>
          <img src="/images/pages/collab-partner.png" alt="Queebi partnership showcase" className="w-full rounded-2xl mb-10" width={800} height={400} loading="lazy" />
          <div className="text-center">
            <Button asChild size="lg" className="rounded-full bg-primary text-primary-foreground font-bold transition-transform duration-200 hover:scale-105">
              <a href="mailto:hello@queebi.com.tw?cc=nindy%40babylux.com.tw" onClick={() => trackCTAClick('collab_email')}>{t("collaborate.collabWithUs")}</a>
            </Button>
          </div>
        </div>
      </AnimatedSection>

      <section className="bg-queebi-sky py-20 md:py-32">
        <div className="container max-w-3xl text-center">
          <p className="text-2xl md:text-3xl font-bold font-heading mb-6">{t("collaborate.whyPartner")}</p>
          <p className="text-xl md:text-2xl font-semibold text-accent mb-10">{t("collaborate.letsBuild")}</p>
          <Button asChild size="lg" className="rounded-full bg-foreground text-background font-bold hover:bg-foreground/90 transition-transform duration-200 hover:scale-105">
            <a href="mailto:hello@queebi.com.tw?cc=nindy%40babylux.com.tw" onClick={() => trackCTAClick('collab_contact')}>{t("collaborate.contactUs")}</a>
          </Button>
        </div>
      </section>

      <FaqGroupsSection title={t("faq.title")} groups={collabGroups} />
    </>
  );
};

export default Collaborate;
